import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Plus, Trash2, Download, FolderOpen } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { TL4KAppHeader } from '@tl4k/admin-ui';
import type { Project } from '../types';
import { getStoredProjects, saveProject, deleteProject, exportProjectsToFile } from '../services/storageService';

function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);

  // Load projects from localStorage on mount
  useEffect(() => {
    const storedProjects = getStoredProjects();
    setProjects(storedProjects);
  }, []);

  // Handle project deletion
  const handleDeleteProject = (e: React.MouseEvent, projectId: string) => {
    e.stopPropagation();
    if (confirm('Sei sicuro di voler eliminare questo progetto?')) {
      deleteProject(projectId);
      setProjects(getStoredProjects());
    }
  };

  // Handle export all projects
  const handleExportAll = () => {
    exportProjectsToFile(projects);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Create a new project with the uploaded file
    const newProject: Project = {
      id: uuidv4(),
      name: file.name.replace(/\.[^/.]+$/, ''),
      backgroundImage: '', // Will be set after processing
      backgroundImageType: file.type === 'application/pdf' ? 'pdf' : 'image',
      width: 800,
      height: 600,
      elements: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Save to localStorage immediately
    saveProject(newProject);
    setProjects(getStoredProjects());
    navigate(`/editor/${newProject.id}`);
  };

  // Create a blank new project
  const handleCreateBlankProject = () => {
    const newProject: Project = {
      id: uuidv4(),
      name: `Progetto ${projects.length + 1}`,
      backgroundImage: '',
      backgroundImageType: 'image',
      width: 800,
      height: 600,
      elements: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    saveProject(newProject);
    setProjects(getStoredProjects());
    navigate(`/editor/${newProject.id}`);
  };

  return (
    <div className="min-h-screen bg-surface-warm">
      {/* TL4K Standard Header */}
      <TL4KAppHeader
        appName="Magic Page"
        surface="neutral"
        subtitle="Trasforma i tuoi materiali didattici"
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* New Project Card */}
        <div className="mb-8">
          <label className="block cursor-pointer">
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <div className="bg-white border-2 border-dashed border-primary-300 rounded-xl p-8 text-center hover:border-primary-500 transition-colors">
              <Upload className="w-12 h-12 mx-auto text-primary-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Carica il tuo materiale
              </h2>
              <p className="text-gray-500 mb-4">
                Trascina qui il tuo file o clicca per selezionare
              </p>
              <p className="text-sm text-gray-400">
                Supportati: PNG, JPEG, PDF
              </p>
            </div>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={handleCreateBlankProject}
            className="flex-1 bg-white border-2 border-primary-300 rounded-xl p-6 text-center hover:border-primary-500 hover:bg-primary-50 transition-all"
          >
            <Plus className="w-8 h-8 mx-auto text-primary-500 mb-2" />
            <span className="font-medium text-gray-700">Nuovo progetto vuoto</span>
          </button>
          {projects.length > 0 && (
            <button
              onClick={handleExportAll}
              className="flex-1 bg-white border-2 border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 hover:bg-gray-50 transition-all"
            >
              <Download className="w-8 h-8 mx-auto text-gray-500 mb-2" />
              <span className="font-medium text-gray-700">Esporta tutti</span>
            </button>
          )}
        </div>

        {/* Projects Grid */}
        {projects.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-700">
                I tuoi progetti ({projects.length})
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group"
                >
                  <div
                    onClick={() => navigate(`/editor/${project.id}`)}
                    className="aspect-video bg-gray-100 flex items-center justify-center cursor-pointer relative"
                  >
                    {project.backgroundImage ? (
                      <img
                        src={project.backgroundImage}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <>
                        <FolderOpen className="w-12 h-12 text-gray-300" />
                        <span className="absolute bottom-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded">
                          {project.elements.length} elementi
                        </span>
                      </>
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div
                      onClick={() => navigate(`/editor/${project.id}`)}
                      className="flex-1 cursor-pointer"
                    >
                      <h3 className="font-medium text-gray-800 truncate">{project.name}</h3>
                      <p className="text-sm text-gray-500">
                        {new Date(project.updatedAt).toLocaleDateString('it')} · {project.width}×{project.height}px
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/player/${project.id}`);
                        }}
                        className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="Riproduci"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-play"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                      </button>
                      <button
                        onClick={(e) => handleDeleteProject(e, project.id)}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="Elimina progetto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {projects.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600 mb-2">
              Nessun progetto ancora
            </h3>
            <p className="text-gray-400 mb-6">
              Carica il tuo primo materiale didattico o crea un progetto vuoto
            </p>
            <button
              onClick={handleCreateBlankProject}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Crea progetto vuoto
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
